import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormGroup, Typography, Paper, Box, Divider, Grid, Chip, Button, Tooltip, TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material'
import { connectorData, connectorStatus, stopReason } from '../../common/constants';
import { connectedStatuses } from '../../Config/charge-point-settings';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';

const animate = '../arrows.gif'

const startableStatuses = [connectorStatus.Available, connectorStatus.Preparing, connectorStatus.Reserved]

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
    },
  },
});

const StyledButton = styled(Button)({  
  borderTopLeftRadius: 0,
  borderBottomLeftRadius: 0,
  width: 40,
  minWidth: 40,
  maxHeight: 40,
  padding: 6,
  '& .MuiButton-startIcon': { margin: 0 }
});


const Connector = ({ id, status, settings, onStartTransaction, onStopTransaction, onStatusNotification, onSendMeterValues, onChange }) => {
  const [ meterError, setMeterError ] = useState(false)
  const [ socError, setSocError ] = useState(false)
  const [ powerError, setPowerError ] = useState(false)
  const [ localStatus, setLocalStatus ] = useState(settings.status)
  const isConnected = connectedStatuses.includes(status.status)
  // No prior Authorize needed: the CSMS accepts or rejects the id tag in the StartTransaction answer.
  const canStart = isConnected && !settings.inTransaction && startableStatuses.includes(settings.status)

  // The Status select follows the connector's real status (start / stop transaction, CSMS
  // commands, ...), so a later Status Notification never sends a stale value.
  useEffect(() => setLocalStatus(settings.status), [settings.status])

  const updateData = (field, data) => {
    if (field === 'currentMeterValue') {
      data = Number(data)
      if(isNaN(data) || !Number.isInteger(data)) return
      settings.startMeterValue > data ? setMeterError(true) : setMeterError(false)
    }

    if (field === 'soc') {
      data = Number(data)
      if(isNaN(data) || !Number.isInteger(data)) return
      data < 0 || data > 100 ? setSocError(true) : setSocError(false)
    }

    if (field === 'requestedPowerW') {
      // Empty: charge at the connector maximum.
      if (String(data).trim() === '') {
        setPowerError(false)
        onChange(field, undefined)
        return
      }
      data = Number(data)
      if (isNaN(data) || data < 0) return setPowerError(true)
      setPowerError(false)
    }

    onChange(field, data)
  }

  return (
    <Paper sx={{p: 2}}>
    <Box display='flex' alignItems='center' justifyContent='space-between'>
      <Typography variant='h6' color='primary'>CONNECTOR - {id}</Typography>
      { settings.inTransaction
        ? <Tooltip placement='top' title='In Transaction' arrow><img src={animate} style={{height: 10}} alt='charge animation' /></Tooltip>
        : null
      }
      <Chip
          size='small'
          label={connectorData[settings.status].text.toUpperCase()}
          sx={{ backgroundColor: connectorData[settings.status].backgroundColor, color: connectorData[settings.status].color}}
        />
    </Box>
    <Divider sx={{ mt: 0.5, mb: 1.5 }} />
    <Grid container spacing={3}>
      <Grid item xs={6}>
        <TextField
          fullWidth
          disabled
          value={settings.idTag}
          label='ID Tag'
          size='small'
        />
      </Grid>
      <Grid item xs={6}>
        <Button
          fullWidth
          variant='contained'
          disabled={!canStart}
          onClick={onStartTransaction}
        >
          Start transaction
        </Button>
      </Grid>
      <Grid item xs={6}>
        <FormControl fullWidth>
          <InputLabel>Stop Reason</InputLabel>
          <Select
            fullWidth
            value={settings.stopReason}
            label='Stop Reason'
            size='small'
            disabled={!settings.inTransaction}
            name='stopReason'
            onChange={(e) => updateData(e.target.name, e.target.value)}
          >
            { Object.keys(stopReason).map(x => <MenuItem key={x} value={stopReason[x]}>{stopReason[x]}</MenuItem>) }
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={6}>
        <Button fullWidth variant='contained' disabled={!settings.inTransaction || !isConnected} onClick={onStopTransaction} > stop transaction </Button>
      </Grid>
      <Grid item xs={6}>
        <FormControl fullWidth>
          <InputLabel>Status</InputLabel>
          <Select
            fullWidth
            value={localStatus}
            label='Status'
            size='small'
            name='status'
            onChange={(e) => setLocalStatus(e.target.value)}
          >
            { Object.keys(connectorStatus).map(x => <MenuItem key={x} value={connectorStatus[x]}>{connectorData[x].text}</MenuItem>) }
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={6}>
        <Button fullWidth variant='contained' disabled={!isConnected} onClick={() => onStatusNotification(localStatus)}>status notification</Button>
      </Grid>
      <Grid item xs={6}>
        <TextField label='Start Meter Value' size='small' variant='outlined' fullWidth value={settings.startMeterValue} disabled />
      </Grid>
      <Grid item xs={6}>
        <FormGroup row>
          <StyledTextField
            disabled={!settings.inTransaction}
            label='SoC (%)'
            size='small'
            error={socError}
            variant='outlined'
            sx={{ width: 'calc(100% - 40px)'}}
            value={settings.soc}
            name='soc'
            onChange={(e) => updateData(e.target.name, e.target.value)}
            onFocus={event => {event.target.select()}}
          />
          <StyledButton
            disabled={!settings.inTransaction}
            variant='contained'
            startIcon={<AddIcon />}
            onClick={() => updateData('soc', Math.min(settings.soc + 10, 100))}
          />
        </FormGroup>
      </Grid>
      <Grid item xs={6}>
        <FormGroup row>
          <StyledTextField
            disabled={!settings.inTransaction}
            label='Current Meter Value'
            size='small'
            error={meterError}
            variant='outlined'
            sx={{ width: 'calc(100% - 40px)'}}
            value={settings.currentMeterValue}
            name='currentMeterValue'
            onChange={(e) => updateData(e.target.name, e.target.value)}
            onFocus={event => {event.target.select()}}
          />
          <StyledButton
            disabled={!settings.inTransaction}
            variant='contained'
            startIcon={<AddIcon />}
            onClick={() => updateData('currentMeterValue', settings.currentMeterValue + 10)}
          />
        </FormGroup>
      </Grid>
      <Grid item xs={6}>
        <TextField
          label='Charging power (W)'
          size='small'
          variant='outlined'
          fullWidth
          error={powerError}
          placeholder={`max ${settings.maxPowerW}`}
          InputLabelProps={{ shrink: true }}
          value={settings.requestedPowerW ?? ''}
          helperText={settings.inTransaction ? `Now ${settings.powerW} W` : `Meter +${settings.requestedPowerW ?? settings.maxPowerW} Wh per hour`}
          onChange={(e) => updateData('requestedPowerW', e.target.value)}
          onFocus={event => {event.target.select()}}
        />
      </Grid>
      <Grid item xs={6}>
      <Button disabled={!settings.inTransaction || !isConnected} fullWidth variant='contained' onClick={onSendMeterValues} >Send Meter Value</Button>
      </Grid>
    </Grid>
  </Paper>
  )
}

Connector.propTypes = {
  id: PropTypes.number.isRequired,
  status: PropTypes.any.isRequired,
  settings: PropTypes.object.isRequired,
  onStartTransaction: PropTypes.func.isRequired,
  onStopTransaction: PropTypes.func.isRequired,
  onStatusNotification: PropTypes.func.isRequired,
  onSendMeterValues: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default Connector